import { useEffect, useState } from "react";
import { useAuth } from "@/auth-provider";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Mail, Lock, ArrowRight, ShieldCheck } from "lucide-react";
import { AuthCard, AuthInput, PasswordField, AuthButton } from "@/components/auth/auth-form";
import { Badge } from "@/components/ui/badge";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const justReset = params.get("reset") === "true";
  const justVerified = params.get("verified") === "true";

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch {
      setError("That email and password combination didn't work.");
      setLoading(false);
    }
  };

  if (user === undefined) {
    return (
      <div className="text-center text-sm text-muted-foreground py-10">Loading…</div>
    );
  }

  return (
    <AuthCard
      eyebrow="Sign in"
      title="Welcome back"
      subtitle="Pick up your locations, units, and saved preferences."
      badge={
        <Badge variant="outline" size="sm" className="!gap-1.5">
          <ShieldCheck className="h-2.5 w-2.5 text-emerald-400" />
          Encrypted
        </Badge>
      }
      footer={
        <p className="text-center text-xs text-muted-foreground">
          New here?{" "}
          <Link to="/register" className="text-primary font-semibold hover:underline">
            Create an account
          </Link>
        </p>
      }
    >
      {(justReset || justVerified) && (
        <div className="mb-5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-2.5 text-xs text-emerald-300">
          {justReset
            ? "Password updated. Sign in with your new password."
            : "Email verified. You're all set — sign in below."}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <AuthInput
          label="Email"
          type="email"
          autoComplete="email"
          required
          icon={<Mail className="h-4 w-4" />}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
        />

        <PasswordField
          label="Password"
          autoComplete="current-password"
          required
          icon={<Lock className="h-4 w-4" />}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          hint={
            <Link
              to="/forgot-password"
              className="text-primary font-semibold hover:underline normal-case tracking-normal"
            >
              Forgot?
            </Link>
          }
        />

        <label className="flex items-center gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="h-4 w-4 rounded border-border bg-background/50 text-primary focus:ring-2 focus:ring-primary/30 cursor-pointer"
          />
          <span className="text-xs text-muted-foreground">Keep me signed in on this device</span>
        </label>

        {error && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3.5 py-2.5 text-xs text-rose-300 animate-slide-up">
            {error}
          </div>
        )}

        <AuthButton type="submit" loading={loading}>
          Sign in <ArrowRight className="h-4 w-4" />
        </AuthButton>
      </form>
    </AuthCard>
  );
}
